import { SyncMessageType, ServerMessage } from "../../shared/messageTypes";
import { ConnectionToServer } from "./networking/connectionToServer";

//What this does is narrow a discriminated union, so for example given T is a SyncMessageType, this will
//infer that the message containing T is the associated message type. 
type NarrowedMessage<T> = Extract<ServerMessage, { messageType: T }>;

export class MessageBuffer {
	buffer = new Array<ServerMessage>();
	unorderedBuffer = new Array<ServerMessage>();

	constructor(comm: ConnectionToServer) {
		comm.addListener(this.onServerMessage);
	}

	clear() {
		this.buffer = [];
		this.unorderedBuffer = [];
	}

	onServerMessage = (messages: ServerMessage[]) => {
		if (messages.length > 0) {
			messages.forEach(messageJson => {
				const msg: ServerMessage = messageJson;
				//console.debug(msg);
				if (msg.noQueue) {
					this.unorderedBuffer.push(msg);
				}
				else {
					this.buffer.push(msg);
				}
			});
		}
	}

	dequeueMessage<T extends SyncMessageType>(type: T, step?: number): NarrowedMessage<T> | null {
		if (this.buffer.length === 0) return null;

		const first = this.buffer[0];

		if (first.messageType !== type) return null;

		if (step !== undefined
			&& (first as any).stepNumber !== undefined
			&& (first as any).stepNumber > step
		) {
			return null;
		}

		this.buffer.shift();
		return first as NarrowedMessage<T>;
	}

	peakMessage<T extends SyncMessageType>(type: T): NarrowedMessage<T> | null {
		if (this.buffer.length === 0) return null;
		const first = this.buffer[0];
		if (first.messageType !== type) return null;
		return first as NarrowedMessage<T>;
	}

	getFirstType() {
		if (this.buffer.length === 0) return null;
		return this.buffer[0].messageType;
	}

	popFirstMessage() {
		if (this.buffer.length === 0) return null;
		return this.buffer.shift()!;
	}

	hasCompleteStep(): boolean {
		return this.buffer.some(m => m.messageType === SyncMessageType.StepEnd || m.messageType === SyncMessageType.GotoShop || m.messageType === SyncMessageType.GotoLobby);
	}

	getUnorderedMessage<T extends SyncMessageType>(type: T, step?: number): NarrowedMessage<T> | null {
		const i = this.unorderedBuffer.findIndex(m => {
			const msg = m as NarrowedMessage<T>;
			return msg.messageType === type && (step === undefined || (msg as any).stepNumber === step);
		});
		if (i > -1) {
			const message = this.unorderedBuffer[i];
			this.unorderedBuffer.splice(i, 1);
			return message as NarrowedMessage<T>;
		}
		return null;
	}

	bufferLength() {
		return this.buffer.length;
	}
}
